import React, { ReactNode, useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form } from 'antd';

// Shared UI Library Components
import { ChipsInput, CustomButton } from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './countries-tab.scss';

// Store entities
import { fetchCompanyAllAvailableCountries, updateCompanyCountries } from '../../../../store/company/company.thunks';
import { getCoreState } from '../../../../store/core/core.selectors';

// Components
import { CountryOption } from '../../country-option/country-option';

// Models
import { ICountry } from '../../../../interfaces/core.interfaces';

export type TCountriesTabRef = {
  isHasChanges: () => boolean;
  resetState: () => void;
};

type TCountriesTabProps = {};

export const CountriesTab = forwardRef<TCountriesTabRef, TCountriesTabProps>((
    {}: TCountriesTabProps, ref
  ) => {

    const [form] = Form.useForm<{
      countries: string[];
    }>();

    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [savingChanges, setSavingChanges] = useState<boolean>(false);
    const [allCountries, setAllCountries] = useState<ICountry[]>([]);

    const {countries: countriesCompany} = useSelector(getCoreState);

    const dispatch = useDispatch<any>();

    const initialValues = countriesCompany.list.map(s => s.name);

    const loadAllCountries = async (): Promise<void> => {
      const {payload} = await dispatch(fetchCompanyAllAvailableCountries());
      if (!payload) return;
      setAllCountries(payload);
    };

    const generateOptions = (options: {name: string, id?: number, code?: string}[], isCountry: boolean = false): {
      label: ReactNode,
      value: string,
      disabled?: boolean,
    }[] => {
      return options.map((option) => (
        {label: isCountry ? <CountryOption country={option as ICountry}/> : option.name, value: option.name})
      )
    };

    const countriesOptions = generateOptions(allCountries, true);

    const onValuesChange = (_: {countries: string[]}, {countries}: {countries: string[]}): void => {
      const hasChanges = !countries.every(country => initialValues.includes(country)) || countries.length !== initialValues.length;
      setHasChanges(hasChanges);
    }

    const saveChanges = async (): Promise<void> => {
      const {countries} = form.getFieldsValue();
      setSavingChanges(true);
      const {payload} = await dispatch(updateCompanyCountries(
        allCountries.filter(country => countries.includes(country.name)).map(country => country.code)
      ));
      setSavingChanges(false);
      if (!payload) return;
      setHasChanges(false);
      form.resetFields();
    }

    useImperativeHandle(ref, () => ({
      isHasChanges(): boolean {
        return hasChanges;
      },
      resetState(): void {
        form.setFieldValue('countries', initialValues);
        setSavingChanges(false);
        setHasChanges(false);
      }
    }));

    useEffect(() => {
      loadAllCountries();
    }, []);

    return (
      <div className="countries-tab">
        <div className="countries-tab--description business--description">
          <p>In this section, you can select the countries that your Consultants are working in.
            If your business operates within a single country, and your clients are also limited to the same country, this
            field is not mandatory to fill.</p>

          <p>It's important to note that the countries added here will automatically appear in the Consultant Addition Form
            and will be used for filtering the Consultant list in the "Consultants" section of your Admin Panel and in the
            Consultant list in the applications for your clients.</p>
        </div>
        <Form className="countries-tab--body business--body-items" form={form} onValuesChange={onValuesChange}>
          {!countriesCompany.isLoad &&
            <div className="body-item chips-items">
              <Form.Item name="countries" initialValue={initialValues}>
                <ChipsInput options={countriesOptions}
                            placeholder={'Country(ies)'}
                            hint={'Start typing to add a country to your list'}
                />
              </Form.Item>

              {hasChanges && <div className="chips-items--actions">
                <CustomButton size={'small'} loading={savingChanges} text={'Save'} onClick={saveChanges}/>
              </div>}
            </div>
          }
        </Form>
      </div>
    );
  }
)
